import React, { useEffect, useState } from 'react';
import { getViewportInfo } from '../utils/responsiveHelper';
import './DeviceIndicator.css';

/**
 * Device Indicator Component
 * Shows current device type, viewport dimensions, and orientation
 * Only shows in development (localhost)
 */
function DeviceIndicator({ inline = false }) {
  const [info, setInfo] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // If inline mode, always show (even in production)
    if (inline) {
      setIsVisible(true);
    } else {
      // Only show in development for non-inline mode
      const isDev = window.location.hostname === 'localhost' || 
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname.startsWith('192.168.') ||
                    window.location.hostname.startsWith('10.');

      if (!isDev) return;

      // Check if user wants to show it (stored in localStorage)
      const stored = localStorage.getItem('showDeviceIndicator');
      setIsVisible(stored === 'true');
    }

    const updateInfo = () => {
      setInfo(getViewportInfo());
    };

    updateInfo();
    window.addEventListener('resize', updateInfo);
    window.addEventListener('orientationchange', updateInfo);

    return () => {
      window.removeEventListener('resize', updateInfo);
      window.removeEventListener('orientationchange', updateInfo);
    };
  }, [inline]);

  const toggleVisibility = () => {
    const newState = !isVisible;
    setIsVisible(newState);
    localStorage.setItem('showDeviceIndicator', newState.toString());
  };

  if (!info || !isVisible) {
    // If inline mode, don't show toggle button
    if (inline) return null;
    
    // Show a small toggle button if hidden
    const isDev = window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1' ||
                  window.location.hostname.startsWith('192.168.') ||
                  window.location.hostname.startsWith('10.');
    
    if (!isDev) return null;

    return (
      <button
        className="device-indicator-toggle"
        onClick={toggleVisibility}
        title="Show device info"
      >
        📱
      </button>
    );
  }

  // Inline compact version for headers
  if (inline) {
    // Try to detect device model from user agent
    const getUserAgentInfo = () => {
      const ua = navigator.userAgent || '';
      let deviceModel = '';
      
      // Samsung Galaxy devices
      if (ua.match(/SM-[A-Z0-9]+/)) {
        const match = ua.match(/SM-([A-Z0-9]+)/);
        if (match) {
          const model = match[1];
          // Common Samsung models
          const modelMap = {
            'S938': 'S25 Ultra',
            'S937': 'S25+',
            'S936': 'S25',
            'S928': 'S24 Ultra',
            'S927': 'S24+',
            'S926': 'S24',
            'S918': 'S23 Ultra',
            'S917': 'S23+',
            'S916': 'S23',
            'S908': 'S22 Ultra',
            'S907': 'S22+',
            'S906': 'S22',
            'S901': 'S21',
            'S991': 'S21+',
            'S996': 'S21 Ultra',
          };
          // Try to extract model number (e.g., S25 from SM-S936B)
          if (modelMap[model]) {
            deviceModel = modelMap[model];
          } else {
            // Try to extract S series number (e.g., S25 from SM-S936)
            const sMatch = model.match(/^S(\d{2})/);
            if (sMatch) {
              deviceModel = `S${sMatch[1]}`;
            } else {
              deviceModel = model;
            }
          }
        }
      }
      // iPhone detection
      else if (ua.match(/iPhone/)) {
        const match = ua.match(/iPhone\s*OS\s*(\d+)_(\d+)/);
        if (match) {
          const major = parseInt(match[1]);
          // Rough iPhone model detection based on iOS version
          if (major >= 17) deviceModel = 'iPhone 15';
          else if (major >= 16) deviceModel = 'iPhone 14';
          else if (major >= 15) deviceModel = 'iPhone 13';
          else deviceModel = 'iPhone';
        } else {
          deviceModel = 'iPhone';
        }
      }
      // Skip generic Android - don't show "android" in display
      // else if (ua.match(/Android/)) {
      //   deviceModel = 'Android';
      // }
      
      return deviceModel;
    };
    
    const deviceModel = getUserAgentInfo();
    
    // Format model name to lowercase (e.g., "S25" -> "s25")
    const formattedModel = deviceModel ? deviceModel.toLowerCase().replace(/\s+/g, '') : '';
    
    // Remove "android" and "mobile" from the display
    const displayText = formattedModel 
      ? `${formattedModel} ${info.deviceType} ${info.width}x${info.height}/${info.orientation}`
      : `${info.deviceType} ${info.width}x${info.height}/${info.orientation}`;
    
    return (
      <div className="device-indicator-inline">
        <span className="device-indicator-inline-text">
          {displayText}
        </span>
      </div>
    );
  }

  const activeBreakpoints = Object.entries(info.breakpoint)
    .filter(([_, active]) => active)
    .map(([name]) => name)
    .join(', ');

  return (
    <div className="device-indicator">
      <div className="device-indicator-header">
        <strong>Device Info</strong>
        <button 
          className="device-indicator-close"
          onClick={toggleVisibility}
          title="Hide"
        >
          ×
        </button>
      </div>
      <div className="device-indicator-content">
        <div className="device-indicator-row">
          <span className="device-indicator-label">Type:</span>
          <span className="device-indicator-value device-type">{info.deviceType}</span>
        </div>
        <div className="device-indicator-row">
          <span className="device-indicator-label">Viewport:</span>
          <span className="device-indicator-value">{info.width} × {info.height}px</span>
        </div>
        <div className="device-indicator-row">
          <span className="device-indicator-label">Orientation:</span>
          <span className="device-indicator-value">{info.orientation}</span>
        </div>
        <div className="device-indicator-row">
          <span className="device-indicator-label">Pixel Ratio:</span>
          <span className="device-indicator-value">{info.pixelRatio}x</span>
        </div>
        <div className="device-indicator-row">
          <span className="device-indicator-label">Breakpoints:</span>
          <span className="device-indicator-value breakpoints">{activeBreakpoints || 'none'}</span>
        </div>
      </div>
    </div>
  );
}

export default DeviceIndicator;

